import type { EpisodeAvailability } from "@anisync/types";
import {
  CheckIcon,
  CircleAlertIcon,
  CloudDownloadIcon,
  EllipsisIcon,
  EyeIcon,
  EyeOffIcon,
  HardDriveIcon,
  LanguagesIcon,
  ListVideoIcon,
  Loader2Icon,
  PlayIcon,
  RotateCwIcon,
  ThumbsDownIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import type { BatchProgressItem } from "../lib/api";
import { formatClock } from "../lib/format";
import { stagger } from "../lib/motion";
import { cn } from "../lib/utils";
import { MediaWarningBadges } from "./media-warnings";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LoadingNote, SLOW_SOURCES_LABEL } from "./ui/loading";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";

export interface EpisodeProgressEntry {
  episodeNumber: number;
  positionSec: number;
  durationSec: number;
  completed: boolean;
}

type Filter = "all" | "unwatched" | "ready";

/** Od ilu odcinków lista dzieli się na zakresy i ile odcinków ma jeden zakres. */
const RANGE_THRESHOLD = 60;
const RANGE_SIZE = 50;

/**
 * Lista odcinków — karta na odcinek, kart tyle obok siebie, ile się mieści.
 *
 * Dwie przebudowy z 12.10.2026, obie ze zgłoszeń „ma być przejrzyściej":
 *
 * Pierwsza zamieniła siatkę kafli z pięcioma przyciskami w każdym na wiersz
 * z jednym przyciskiem i menu „⋯". Druga wzięła się z monitora 4K: wiersz szedł
 * przez całe okno, więc numer odcinka stał przy lewej krawędzi, a przycisk
 * kilka tysięcy pikseli dalej, z pustym pasem pomiędzy. Teraz wiersz jest kartą
 * o ograniczonej szerokości w siatce `row-grid` — na telefonie to nadal jedna
 * kolumna, na szerokim ekranie trzy-cztery obok siebie, czytane jak kalendarz.
 *
 * Karta mówi stan w stałych miejscach: numer (z ptaszkiem po obejrzeniu), stan
 * materiału kolorem i jednym zdaniem, pod spodem obejrzenie i szczegóły. „Odcinek
 * N" nie powtarza się w treści, bo numer stoi obok w polu, po którym oko przebiega
 * listę — czytnik ekranu dostaje go osobno.
 *
 * Długa seria dzieli się na zakresy po pięćdziesiąt: siedemset kart pod rząd
 * to przewijanie bez punktu zaczepienia. Domyślny zakres to ten z następnym
 * odcinkiem do obejrzenia, bo tam się wraca.
 */
export function EpisodeList({
  episodes,
  progress,
  live,
  authenticated,
  removing,
  marking,
  actions,
  onPlay,
  onVersions,
  onTogether,
  onToggleWatched,
  onRemove,
  onWrongVersion,
}: {
  episodes: EpisodeAvailability[];
  progress: Map<number, EpisodeProgressEntry>;
  /** Bieżący postęp materiałów w drodze, po identyfikatorze materiału. */
  live: Map<string, BatchProgressItem>;
  authenticated: boolean;
  removing: string | null;
  marking: number | null;
  /** Przyciski serii („Przygotuj całą serię"…) — stoją w nagłówku listy. */
  actions?: ReactNode;
  onPlay: (episode: EpisodeAvailability) => void;
  onVersions: (episodeNumber: number) => void;
  onTogether: (episodeNumber: number) => void;
  onToggleWatched: (episodeNumber: number, watched: boolean) => void;
  onRemove: (mediaFileId: string) => void;
  onWrongVersion: (mediaFileId: string) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  /** Zakres wybrany ręcznie; `null` — ten z następnym odcinkiem. */
  const [pickedRange, setPickedRange] = useState<number | null>(null);

  const counts = useMemo(() => {
    let ready = 0;
    let pending = 0;
    let missing = 0;
    let watched = 0;
    for (const ep of episodes) {
      if (ep.mediaState === "ready") ready += 1;
      else if (ep.mediaState !== null && ep.mediaState !== "failed") pending += 1;
      if (ep.sources.length === 0 && ep.mediaFileId === null) missing += 1;
      if (progress.get(ep.episodeNumber)?.completed === true) watched += 1;
    }
    return { ready, pending, missing, watched, unwatched: episodes.length - watched };
  }, [episodes, progress]);

  const nextUp = useMemo(() => nextToWatch(episodes, progress), [episodes, progress]);
  const ranges = useMemo(() => episodeRanges(episodes), [episodes]);
  const nextRange = nextUp === null ? -1 : ranges.findIndex((r) => r.includes(nextUp));
  const rangeIndex = Math.min(pickedRange ?? Math.max(nextRange, 0), ranges.length - 1);
  const range = ranges[rangeIndex];

  const visible = (range === undefined ? episodes : episodes.slice(range.start, range.end)).filter(
    (ep) => {
      if (filter === "ready") return ep.mediaState === "ready";
      if (filter === "unwatched") return progress.get(ep.episodeNumber)?.completed !== true;
      return true;
    },
  );

  const summary = [
    `${episodes.length} ${plural(episodes.length, "odcinek", "odcinki", "odcinków")}`,
    counts.ready > 0 ? `${counts.ready} na serwerze` : null,
    counts.pending > 0 ? `${counts.pending} w drodze` : null,
    counts.missing > 0 ? `${counts.missing} bez źródeł` : null,
  ].filter((x): x is string => x !== null);

  const watchedPercent = episodes.length > 0 ? (counts.watched / episodes.length) * 100 : 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h2 className="text-lg font-semibold">Odcinki</h2>
          <p className="text-sm text-muted-foreground">{summary.join(" · ")}</p>
          {/* Licznik obejrzenia odpowiada na „gdzie jestem w tej serii" bez
              przeliczania ptaszków po kartach. Przy zerze byłby pustą kreską. */}
          {authenticated && counts.watched > 0 && (
            <div className="flex items-center gap-3 text-sm">
              <Progress
                value={watchedPercent}
                className="h-1.5 w-40"
                aria-label={`Obejrzane ${counts.watched} z ${episodes.length}`}
              />
              <span className="text-muted-foreground tabular-nums">
                obejrzane {counts.watched} z {episodes.length}
              </span>
            </div>
          )}
        </div>
        {actions !== undefined && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>

      {(episodes.length > 6 || ranges.length > 1) && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {/*
            Filtry jako przyciski z `aria-pressed`, nie zakładki: zmieniają widok tej
            samej listy, a nie przełączają między różnymi treściami. Przy krótkiej
            serii nic nie wnoszą, więc stoją tylko przy dłuższej.
          */}
          {episodes.length > 6 && (
            <fieldset className="m-0 flex flex-wrap gap-1.5 border-0 p-0">
              <legend className="sr-only">Pokaż odcinki</legend>
              <ToggleButton active={filter === "all"} onClick={() => setFilter("all")}>
                Wszystkie
              </ToggleButton>
              {authenticated && (
                <ToggleButton
                  active={filter === "unwatched"}
                  onClick={() => setFilter("unwatched")}
                >
                  Do obejrzenia ({counts.unwatched})
                </ToggleButton>
              )}
              <ToggleButton active={filter === "ready"} onClick={() => setFilter("ready")}>
                Na serwerze ({counts.ready})
              </ToggleButton>
            </fieldset>
          )}

          {ranges.length > 1 && (
            <fieldset className="m-0 flex flex-wrap gap-1.5 border-0 p-0">
              <legend className="sr-only">Zakres odcinków</legend>
              {ranges.map((r, i) => (
                <ToggleButton
                  key={r.label}
                  active={i === rangeIndex}
                  onClick={() => setPickedRange(i)}
                >
                  <span className="tabular-nums">{r.label}</span>
                </ToggleButton>
              ))}
            </fieldset>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {filter === "ready"
            ? "Żaden odcinek z tego zakresu nie jest jeszcze na serwerze."
            : "Wszystko obejrzane."}
        </p>
      ) : (
        <ol className="row-grid gap-2">
          {visible.map((ep, i) => (
            <EpisodeRow
              key={ep.episodeNumber}
              index={i}
              ep={ep}
              seen={progress.get(ep.episodeNumber)}
              live={ep.mediaFileId !== null ? (live.get(ep.mediaFileId) ?? null) : null}
              next={nextUp === ep.episodeNumber}
              authenticated={authenticated}
              removing={ep.mediaFileId !== null && removing === ep.mediaFileId}
              marking={marking === ep.episodeNumber}
              onPlay={() => onPlay(ep)}
              onVersions={() => onVersions(ep.episodeNumber)}
              onTogether={() => onTogether(ep.episodeNumber)}
              onToggleWatched={(watched) => onToggleWatched(ep.episodeNumber, watched)}
              onRemove={() => {
                if (ep.mediaFileId !== null) onRemove(ep.mediaFileId);
              }}
              onWrongVersion={() => {
                if (ep.mediaFileId !== null) onWrongVersion(ep.mediaFileId);
              }}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      size="sm"
      variant={active ? "secondary" : "ghost"}
      aria-pressed={active}
      onClick={onClick}
      className={cn(!active && "text-muted-foreground")}
    >
      {children}
    </Button>
  );
}

function EpisodeRow({
  index,
  ep,
  seen,
  live,
  next,
  authenticated,
  removing,
  marking,
  onPlay,
  onVersions,
  onTogether,
  onToggleWatched,
  onRemove,
  onWrongVersion,
}: {
  /** Pozycja na liście — z niej opóźnienie wejścia karty. */
  index: number;
  ep: EpisodeAvailability;
  seen: EpisodeProgressEntry | undefined;
  live: BatchProgressItem | null;
  next: boolean;
  authenticated: boolean;
  removing: boolean;
  marking: boolean;
  onPlay: () => void;
  onVersions: () => void;
  onTogether: () => void;
  onToggleWatched: (watched: boolean) => void;
  onRemove: () => void;
  onWrongVersion: () => void;
}) {
  const completed = seen?.completed === true;
  const partial =
    seen !== undefined && !completed && seen.positionSec > 0 ? seen.positionSec : null;
  const watchedPercent =
    partial !== null && seen !== undefined && seen.durationSec > 0
      ? Math.min(100, (partial / seen.durationSec) * 100)
      : null;
  const status = statusOf(ep, live);
  // Bez źródła i bez pliku nie ma czego odtworzyć — przycisk prowadziłby wprost
  // do tabeli nieudanych prób.
  const playable = ep.sources.length > 0 || ep.mediaFileId !== null;
  const hasMedia = ep.mediaFileId !== null;
  const canPickVersion = (!hasMedia || ep.mediaState === "failed") && ep.sources.length > 0;
  const warnings = ep.mediaState === "ready" && ep.mediaWarnings.length > 0;
  const hasNotes = next || completed || partial !== null || status.detail !== null || warnings;
  const label = playLabel(ep, partial, completed);

  return (
    <li
      // Karty wchodzą po kolei, kiedy lista przyszła. Przy filtrze wchodzą tylko
      // nowe — te, które zostają, mają ten sam klucz i nie montują się od nowa.
      style={stagger(index)}
      className={cn(
        "enter-rise relative flex min-w-0 items-center gap-3 overflow-hidden rounded-xl border bg-card py-2.5 pr-2 pl-2.5",
        next ? "border-accent/60 bg-accent/[0.06]" : "border-border",
        !playable && "opacity-60",
      )}
    >
      <span className="sr-only">Odcinek {ep.episodeNumber}</span>

      {/* Numer w stałym polu — po nim oko przebiega listę. Obejrzany ma ptaszek
          i zieleń, bo to jedyna rzecz, której widz szuka wzrokiem w całej serii. */}
      <span
        aria-hidden="true"
        className={cn(
          "relative flex size-11 shrink-0 flex-col items-center justify-center rounded-lg leading-none",
          completed
            ? "bg-success/15 text-success"
            : next
              ? "bg-accent/15 text-accent"
              : "bg-secondary text-foreground",
        )}
      >
        <span className="text-[0.625rem] font-medium tracking-wide uppercase opacity-70">odc.</span>
        <span className="mt-0.5 text-base font-bold tabular-nums">{ep.episodeNumber}</span>
        {completed && (
          <CheckIcon className="absolute -right-1 -bottom-1 size-4 rounded-full bg-success p-0.5 text-success-foreground" />
        )}
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p
          className={cn(
            "flex items-start gap-1.5 text-sm leading-tight font-medium",
            TONE_CLASS[status.tone],
          )}
        >
          <status.Icon
            className={cn("mt-px size-4 shrink-0", status.spin === true && "animate-spin")}
          />
          <span className={cn(status.tabular === true && "tabular-nums")}>{status.label}</span>
        </p>

        {hasNotes && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {next && <Badge variant="accent">Następny</Badge>}
            {completed && <span className="text-success">obejrzany</span>}
            {partial !== null && <span>przerwany na {formatClock(partial)}</span>}
            {status.detail !== null && <span className="min-w-0 break-words">{status.detail}</span>}
            {warnings && <MediaWarningBadges warnings={ep.mediaWarnings} />}
          </p>
        )}

        {status.progress !== undefined && (
          <Progress
            value={status.progress}
            className="h-1"
            aria-label={`Odcinek ${ep.episodeNumber}: ${status.label}`}
          />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          size="sm"
          // Gotowy materiał rusza od razu — wyróżniamy to, bo tylko tu kliknięcie
          // daje obraz bez czekania.
          variant={ep.mediaState === "ready" ? "default" : "secondary"}
          disabled={!playable}
          onClick={onPlay}
          aria-label={`${label} — odcinek ${ep.episodeNumber}`}
        >
          {ep.mediaState === "failed" ? <RotateCwIcon /> : <PlayIcon />}
          {label}
        </Button>

        {authenticated && (
          /*
            `modal={false}`: pozycje menu otwierają okna (wersje, „Zła wersja”).
            Menu modalne Radiksa zostawia wtedy `pointer-events: none` na stronie
            po zamknięciu, czyli okno, którego nie da się kliknąć.
          */
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-muted-foreground"
                aria-label={`Więcej czynności — odcinek ${ep.episodeNumber}`}
              >
                <EllipsisIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Odcinek {ep.episodeNumber}</DropdownMenuLabel>
              {/* Wybór wersji, gdy jest w czym wybierać — także po porażce: przy
                  wszystkich kopiach odrzuconych to jedyne miejsce przywracania. */}
              {canPickVersion && (
                <DropdownMenuItem onSelect={onVersions}>
                  <ListVideoIcon />
                  Wybierz wersję…
                </DropdownMenuItem>
              )}
              {playable && (
                <DropdownMenuItem onSelect={onTogether}>
                  <UsersIcon />
                  Oglądaj razem
                </DropdownMenuItem>
              )}
              <DropdownMenuItem disabled={marking} onSelect={() => onToggleWatched(!completed)}>
                {completed ? <EyeOffIcon /> : <EyeIcon />}
                {completed ? "Oznacz jako nieobejrzany" : "Oznacz jako obejrzany"}
              </DropdownMenuItem>

              {hasMedia && <DropdownMenuSeparator />}
              {/* „Zła wersja” to mądrzejsza odmiana „Usuń”: usunięcie pobrałoby przy
                  następnym „Odtwórz” tę samą kopię. Tylko dla gotowego materiału. */}
              {ep.mediaState === "ready" && (
                <DropdownMenuItem onSelect={onWrongVersion}>
                  <ThumbsDownIcon />
                  Zła wersja…
                </DropdownMenuItem>
              )}
              {hasMedia && (
                <DropdownMenuItem
                  disabled={removing}
                  onSelect={onRemove}
                  className="text-destructive data-[highlighted]:text-destructive"
                >
                  <Trash2Icon />
                  {ep.mediaState === "ready" ? "Usuń z serwera" : "Przerwij i usuń"}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Pasek obejrzenia na krawędzi karty — ta sama konwencja co na kaflach
          „Kontynuuj oglądanie", więc nie wymaga podpisu. */}
      {watchedPercent !== null && (
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-0.5 bg-secondary">
          <span
            className="block h-full bg-gradient-to-r from-primary to-accent"
            style={{ width: `${watchedPercent}%` }}
          />
        </span>
      )}
    </li>
  );
}

/**
 * Kształt listy na czas pytania źródeł.
 *
 * Lista odcinków to najwolniejsza odpowiedź w aplikacji: serwer pyta wszystkie
 * włączone źródła, a część z nich pytamy powoli, żeby nas nie zablokowały
 * (Shinden trzyma kilkanaście sekund między żądaniami). Do 12.10.2026 stało
 * w tym czasie jedno zdanie „Sprawdzam źródła…", przez pół minuty bez zmian —
 * nie dało się odróżnić czekania od zawieszenia. Teraz karty mają już swoje
 * miejsca, a po kilku sekundach zdanie mówi, dlaczego trwa to dłużej.
 *
 * Czas liczymy od `since` — chwili wysłania pytania — a nie od zamontowania:
 * strona tytułu podmienia swój szkielet na gotowy nagłówek, a z nim montuje ten
 * komponent drugi raz. Licznik od zera kazałby wtedy widzowi, który czekał już
 * dziesięć sekund, czytać od nowa „Pytam źródła…". Karty szkieletu nie wjeżdżają
 * z tego samego powodu: wjechałyby drugi raz przy tej podmianie, a ruch daje im
 * przebiegające światło.
 */
export function EpisodeListSkeleton({ count, since }: { count: number; since: number }) {
  // Klucze z góry, nie z indeksu w `map` — indeks nie jest kluczem.
  const keys = useMemo(
    () => Array.from({ length: Math.max(1, count) }, (_, i) => `szkielet-${i}`),
    [count],
  );

  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-lg font-semibold">Odcinki</h2>
        <LoadingNote
          label="Pytam źródła o listę odcinków…"
          slowLabel={SLOW_SOURCES_LABEL}
          since={since}
        />
      </div>

      <ol className="row-grid gap-2" aria-hidden="true">
        {keys.map((key) => (
          <li
            key={key}
            className="flex items-center gap-3 rounded-xl border border-border bg-card py-2.5 pr-2 pl-2.5"
          >
            <Skeleton className="size-11 shrink-0 rounded-lg" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-8 w-24" />
          </li>
        ))}
      </ol>
    </div>
  );
}

type Tone = "success" | "progress" | "muted" | "accent" | "error";

const TONE_CLASS: Record<Tone, string> = {
  success: "text-success",
  progress: "text-warning",
  muted: "text-muted-foreground",
  accent: "text-accent",
  error: "text-destructive",
};

interface RowStatus {
  Icon: typeof HardDriveIcon;
  label: string;
  detail: string | null;
  tone: Tone;
  spin?: boolean;
  tabular?: boolean;
  /** Pasek postępu: brak — bez paska, `null` — pasek nieokreślony. */
  progress?: number | null;
}

/**
 * Stan odcinka w jednym zdaniu.
 *
 * Rozróżnia to, co wcześniej wyglądało tak samo: szukanie kopii (do pół minuty
 * z Shinden), czekanie na swoją kolej (pobieramy jeden plik naraz, a paczka serii
 * idzie odcinek po odcinku) i samo pobieranie z procentem. Wyeksportowane dla testu.
 */
export function statusOf(ep: EpisodeAvailability, live: BatchProgressItem | null): RowStatus {
  // Postęp na żywo wyprzedza listę o takt odświeżenia: materiał już gotowy albo
  // już nieudany nie może jeszcze przez sekundę wisieć jako „Pobieram 100%".
  const state = live?.state ?? ep.mediaState;
  if (state === "ready") {
    return { Icon: HardDriveIcon, label: "Na serwerze", detail: null, tone: "success" };
  }
  if (state === "failed") {
    return {
      Icon: CircleAlertIcon,
      label: "Ostatnia próba nieudana",
      detail: ep.sources.length > 0 ? "można ponowić" : null,
      tone: "error",
    };
  }
  if (state !== null) {
    if (state === "queued") {
      return {
        Icon: Loader2Icon,
        label: "W kolejce serii",
        detail: "pobiera się po poprzednim",
        tone: "muted",
        spin: true,
      };
    }
    if (state === "probing" || state === "processing") {
      return {
        Icon: Loader2Icon,
        label: "Przygotowuję do odtwarzania",
        detail: null,
        tone: "progress",
        spin: true,
        progress: live?.stagePercent ?? null,
      };
    }
    if (
      live === null ||
      live.stage === "resolve" ||
      (live.stage === null && live.bytesDone === null)
    ) {
      return {
        Icon: Loader2Icon,
        label: "Szukam kopii w źródłach",
        detail: null,
        tone: "progress",
        spin: true,
        progress: null,
      };
    }
    if (live.waiting) {
      return {
        Icon: Loader2Icon,
        label: "Czeka na wolne miejsce",
        detail: "pobieramy jeden plik naraz",
        tone: "muted",
        spin: true,
      };
    }
    const percent =
      live.bytesTotal !== null && live.bytesTotal > 0 && live.bytesDone !== null
        ? Math.min(100, Math.round((live.bytesDone / live.bytesTotal) * 100))
        : null;
    return {
      Icon: Loader2Icon,
      label: percent !== null ? `Pobieram ${percent}%` : "Pobieram",
      detail:
        live.speedBps !== null && live.speedBps > 0
          ? `${((live.speedBps * 8) / 1_000_000).toFixed(1)} Mb/s`
          : null,
      tone: "progress",
      spin: true,
      tabular: true,
      progress: percent,
    };
  }
  if (ep.sources.length === 0) {
    return { Icon: CircleAlertIcon, label: "Brak w źródłach", detail: null, tone: "muted" };
  }
  if (ep.sources.every((s) => s.fallback)) {
    // Wyłącznie źródła zapasowe: polskiej wersji nie ma. Widz musi wiedzieć o tym
    // PRZED pobraniem kilkuset megabajtów, nie po.
    return {
      Icon: LanguagesIcon,
      label: "Tylko z angielskimi napisami",
      detail: null,
      tone: "accent",
    };
  }
  return {
    Icon: CloudDownloadIcon,
    label: "Do pobrania",
    detail: [...new Set(ep.sources.map((s) => s.sourceLabel))].join(", "),
    tone: "muted",
  };
}

function playLabel(ep: EpisodeAvailability, partial: number | null, completed: boolean): string {
  if (ep.mediaState === "failed") return "Ponów";
  if (partial !== null) return `Od ${formatClock(partial)}`;
  if (completed) return "Jeszcze raz";
  return "Odtwórz";
}

/**
 * Następny do obejrzenia: pierwszy nieobejrzany po najdalszym obejrzanym.
 *
 * Nie „pierwszy nieobejrzany w ogóle" — kto pominął odcinek 3, ogląda dalej od 8,
 * a nie wraca do trzeciego. Bez żadnego obejrzanego nie wskazujemy nic: lista bez
 * historii nie ma „następnego", a oznaczenie pierwszego byłoby oczywistością.
 */
export function nextToWatch(
  episodes: EpisodeAvailability[],
  progress: Map<number, EpisodeProgressEntry>,
): number | null {
  let furthest = 0;
  for (const entry of progress.values()) {
    if (entry.completed || entry.positionSec > 0)
      furthest = Math.max(furthest, entry.episodeNumber);
  }
  if (furthest === 0) return null;
  const started = progress.get(furthest);
  // Przerwany w połowie jest „następnym" sam — do niego się wraca.
  if (started !== undefined && !started.completed) return furthest;
  const next = episodes.find(
    (ep) =>
      ep.episodeNumber > furthest &&
      progress.get(ep.episodeNumber)?.completed !== true &&
      (ep.sources.length > 0 || ep.mediaFileId !== null),
  );
  return next?.episodeNumber ?? null;
}

export interface PrimaryEpisode {
  episode: EpisodeAvailability;
  /** `resume` — przerwany w połowie, `next` — po obejrzanych, `start` — bez historii. */
  kind: "resume" | "next" | "start";
  resumeAt: number | null;
}

/**
 * Odcinek dla głównego przycisku nad listą — „co teraz włączyć".
 *
 * Bez historii to pierwszy odcinek, który da się odtworzyć. Z historią to
 * `nextToWatch`, a gdy go nie ma (seria obejrzana albo następny jeszcze nie wyszedł)
 * przycisku nie ma wcale: „Odtwórz odcinek 1" po obejrzeniu całości byłoby radą
 * od rzeczy.
 */
export function primaryEpisode(
  episodes: EpisodeAvailability[],
  progress: Map<number, EpisodeProgressEntry>,
): PrimaryEpisode | null {
  const next = nextToWatch(episodes, progress);
  if (next !== null) {
    const episode = episodes.find((ep) => ep.episodeNumber === next);
    if (episode === undefined) return null;
    const entry = progress.get(next);
    const resumeAt =
      entry !== undefined && !entry.completed && entry.positionSec > 0 ? entry.positionSec : null;
    return { episode, kind: resumeAt !== null ? "resume" : "next", resumeAt };
  }
  const anyHistory = [...progress.values()].some((e) => e.completed || e.positionSec > 0);
  if (anyHistory) return null;
  const first = episodes.find((ep) => ep.sources.length > 0 || ep.mediaFileId !== null);
  return first === undefined ? null : { episode: first, kind: "start", resumeAt: null };
}

export interface EpisodeRange {
  /** Indeksy w liście: `[start, end)`. */
  start: number;
  end: number;
  label: string;
  includes: (episodeNumber: number) => boolean;
}

/**
 * Podział długiej serii na zakresy po {@link RANGE_SIZE} odcinków.
 *
 * Dzielimy po pozycji na liście, a podpis bierzemy z numerów — numeracja źródeł
 * miewa dziury i odcinek zerowy, więc „1–50" liczone z numerów dawałoby zakresy
 * o różnej liczbie kart. Krótka seria to jeden zakres, czyli brak przełącznika.
 */
export function episodeRanges(episodes: EpisodeAvailability[]): EpisodeRange[] {
  if (episodes.length <= RANGE_THRESHOLD) {
    return [{ start: 0, end: episodes.length, label: "Wszystkie", includes: () => true }];
  }
  const ranges: EpisodeRange[] = [];
  for (let start = 0; start < episodes.length; start += RANGE_SIZE) {
    const end = Math.min(episodes.length, start + RANGE_SIZE);
    const from = episodes[start]?.episodeNumber ?? 0;
    const to = episodes[end - 1]?.episodeNumber ?? from;
    ranges.push({
      start,
      end,
      label: `${from}–${to}`,
      includes: (n) => n >= from && n <= to,
    });
  }
  return ranges;
}

function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const lastTwo = n % 100;
  const last = n % 10;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return few;
  return many;
}
